import { useState, useEffect, useRef } from "react";
import { TextInput } from "@sprout/text_input";
import { type FieldProps } from "./field";
import { fetchEmailSuggestion } from "@utils/jben/email_address_validator.ts";
import { useTranslation } from "react-i18next";

const EmailField = ({ label, name, required, error, onChange, onFocus, value }: FieldProps) => {
  const [suggestedEmail, setSuggestedEmail] = useState<string | null>(null);
  const { t } = useTranslation("job_post");
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (typeof value !== "string" || !value || value.length < 5) {
      setSuggestedEmail(null);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const timer = setTimeout(async () => {
      try {
        const suggestion = await fetchEmailSuggestion(value);
        if (!controller.signal.aborted) setSuggestedEmail(suggestion);
      } catch (e) {
        if (!controller.signal.aborted) {
          console.error(`Failed to fetch email suggestion: ${e}`);
          setSuggestedEmail(null);
        }
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [value]);

  const useSuggestion = () => {
    if (!suggestedEmail) return;
    onChange(suggestedEmail);
    setSuggestedEmail(null);
  };

  const prompt = () => (
    <>
      {t("application.email_suggestion_prompt")}{" "}
      <button className="link" onClick={useSuggestion} data-testid="email-suggestion">
        {suggestedEmail}
      </button>
    </>
  );

  const helpMessage = () => {
    if (!suggestedEmail || error) return null;
    return prompt();
  };

  const errorMessage = () => {
    if (!error) return null;
    if (!suggestedEmail) return error;
    return (
      <>
        {error}
        <br />
        {prompt()}
      </>
    );
  };

  return (
    <div onFocus={onFocus}>
      <TextInput
        id={name}
        label={label}
        required={required}
        onChange={(event) => onChange(event.currentTarget.value || "")}
        help={helpMessage()}
        error={errorMessage()}
        maxLength={255}
        value={(value as string) || ""}
      />
    </div>
  );
};

export default EmailField;
